/**
 * Dashboard Component
 * Main dashboard page that displays ESG metrics overview, performance data,
 * quick actions, and organization statistics
 */

// NextUI Components for UI elements
import {
  Avatar,
  Button,
  Card,
  CardBody,
  CardHeader,
  CircularProgress,
  Chip,
  Input,
  Link,
  Progress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  Tooltip,
  Badge,
  Divider,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@nextui-org/react";
import React, { useEffect, useMemo, useState } from "react";
import {
  getDataWithFilterUrlwithLimit,
  getFilterDataWithPagination,
  getOnedata,
  getDataWithFilterUrl,
  StringLimit,
} from "../../controllers/APIController";

// Custom components
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import { useNavigate, useParams } from "react-router-dom";
import { DateFormatorDD } from "../../utils/helper";
import { Colors, numColors } from "../../utils/constant";
import NullData from "../../components/ErrorPage/NullData";
import { PageLoader } from "../../components/Loader";


// FontAwesome icons for UI elements
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faPlus,
  faEye,
  faFileAlt,
  faCog,
  faUsers,
  faBuilding,
  faCalendarAlt,
  faArrowUp,
  faArrowDown,
  faMinus,
  faFilter,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";

// Import chart components
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import { useQuery } from "@tanstack/react-query";

const MetricSummaryCard = ({
  title,
  value,
  change,
  icon,
  color,
  subtitle,
  trend,
}) => {
  const getTrendIcon = () => {
    if (trend === "up")
      return (
        <FontAwesomeIcon icon={faArrowUp} className="text-green-500 text-xs" />
      );
    if (trend === "down")
      return (
        <FontAwesomeIcon icon={faArrowDown} className="text-red-500 text-xs" />
      );
    return <FontAwesomeIcon icon={faMinus} className="text-gray-500 text-xs" />;
  };

  return (
    <Card className="bg-white border border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
      <CardBody className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {/* Metric title and trend indicator */}
            <div className="flex items-center gap-2 mb-1">
              <p className="text-xs text-neutral-600 font-medium">{title}</p>
              {trend && getTrendIcon()}
            </div>

            {/* Metric value and change percentage */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl font-bold text-neutral-900">
                {value}
              </span>
              {change !== undefined && (
                <Chip
                  size="sm"
                  color={
                    change > 0 ? "success" : change < 0 ? "danger" : "default"
                  }
                  variant="flat"
                  className="text-xs"
                >
                  {change > 0 ? "+" : ""}
                  {change}%
                </Chip>
              )}
            </div>

            {/* Optional subtitle */}
            {subtitle && <p className="text-xs text-neutral-500">{subtitle}</p>}
          </div>
          <div
            className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}
          >
            <FontAwesomeIcon
              icon={icon}
              className={`text-${color}-600 text-sm`}
            />
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

const QuickActionCard = ({
  title,
  description,
  icon,
  color,
  onClick,
  badge,
}) => {
  return (
    <Card
      className="bg-white border border-neutral-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      isPressable
      onPress={onClick}
    >
      <CardBody className="p-4">
        <div className="flex items-start gap-3">
          <div
            className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center flex-shrink-0`}
          >
            <FontAwesomeIcon
              icon={icon}
              className={`text-${color}-600 text-sm`}
            />
          </div>

          {/* Content area */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-semibold text-neutral-900">
                {title}
              </h4>
              {badge && <Badge content={badge} color="primary" size="sm" />}
            </div>
            <p className="text-xs text-neutral-600">{description}</p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

/**
 * Performance Radar Chart Component
 * Displays performance data in a radar chart format for ESG categories
 *
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of performance data objects
 * @returns {JSX.Element} Radar chart component
 */
const PerformanceRadarChart = ({ data }) => {
  // Show message if no data available
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-neutral-500">
        <p className="text-sm">No performance data available</p>
      </div>
    );
  }

  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data}>
          <PolarGrid />
          <PolarAngleAxis dataKey="subject" />
          <PolarRadiusAxis angle={90} domain={[0, 100]} />

          {/* Current performance line */}
          <Radar
            name="Current Performance"
            dataKey="A"
            stroke="#059669"
            fill="#059669"
            fillOpacity={0.3}
          />

          {/* Target performance line */}
          <Radar
            name="Target"
            dataKey="B"
            stroke="#3b82f6"
            fill="transparent"
            strokeDasharray="5 5"
          />

          <RechartsTooltip />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Main Dashboard Component
 * Provides an overview of ESG metrics, performance data, and quick actions
 *
 * @returns {JSX.Element} Dashboard page component
 */
const Dashboard = () => {
  // Navigation and routing hooks
  const navigator = useNavigate();
  const { _orgId: currOrg } = useParams();

  // State management for dashboard data
  const [responseData, setResponseData] = useState([]);
  // const [categoryName, setCategoryName] = useState({})
  const [UserDetails, setUserDetails] = useState(null);
  const [Loader, setLoader] = useState(true);

  // Metrics summary state
  const [metricsSummary, setMetricsSummary] = useState({
    totalMetrics: 0,
    completedMetrics: 0,
    inProgressMetrics: 0,
    pendingMetrics: 0,
    completionRate: 0,
  });

  // Activity and chart data state
  const [recentActivity, setRecentActivity] = useState([]);
  const [chartData, setChartData] = useState([]);

  // Organization statistics state
  const [organizationStats, setOrganizationStats] = useState({
    totalUsers: 0,
    totalOrganizations: 0,
    activeProjects: 0,
    lastUpdated: null,
  });

  // Additional dashboard data state
  const [topCategories, setTopCategories] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [lastDataRefresh, setLastDataRefresh] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [selectedTimeframe, setSelectedTimeframe] = useState("month");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  
  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const UserId = UserLogged && UserLogged.user.id;
  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + OrgInfo;
  const dummyurl = ""


  
  const { data: userDetails, isPending } = useQuery({
    queryKey: ["users", UserId],
    queryFn: () => getOnedata("users", UserId),
  });

  const { data: usersData } = useQuery({
    queryKey: ["users", orgFilterUrl],
    queryFn: () => getDataWithFilterUrl("users", orgFilterUrl),
  });

  const { data: orgData } = useQuery({
    queryKey: ["organizations", orgFilterUrl],
    queryFn: () => getDataWithFilterUrl("organizations", orgFilterUrl),
  });

  const { data: metricsData } = useQuery({
    queryKey: ["metrics", orgFilterUrl],
    queryFn: () => getDataWithFilterUrl("metrics", orgFilterUrl),
  });

  const { data: workflowsData, ispending} = useQuery({
    queryKey: ["workflows", dummyurl],
    queryFn: () => getOnedata("workflows", dummyurl),
  });
  // Fetch metrics summary and real data
  useEffect(() => {
    setUserDetails(userDetails);
    setWorkflows(workflowsData);
  }, [userDetails, workflowsData]);



  // Fetch metrics summary and real data
  useEffect(() => {
    // Fetch metrics data

    const metrics = metricsData?.data || [];

    const totalMetrics = metrics.length;
    const completedMetrics = metrics.filter(
      (m) =>
        (m?.attributes?.currentCounter || 0) >=
        (m?.attributes?.requiredCounter || 1)
    ).length;
    const inProgressMetrics = metrics.filter((m) => {
      const current = m?.attributes?.currentCounter || 0;
      const required = m?.attributes?.requiredCounter || 1;
      return current > 0 && current < required;
    }).length;
    const pendingMetrics = totalMetrics - completedMetrics - inProgressMetrics;
    const completionRate =
      totalMetrics > 0
        ? Math.round((completedMetrics / totalMetrics) * 100)
        : 0;

    setMetricsSummary({
      totalMetrics,
      completedMetrics,
      inProgressMetrics,
      pendingMetrics,
      completionRate,
    });

    // Generate real chart data based on timeframe
    const timeframeData = generateTimeframeChartData(
      metrics,
      selectedTimeframe
    );
    setChartData(timeframeData);

    // Calculate top categories
    const categoryCounts = {};
    metrics.forEach((metric) => {
      const category = metric?.attributes?.Category || "Unknown";
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });

    const topCategoriesData = Object.entries(categoryCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([category, count]) => ({ category, count }));

    setTopCategories(topCategoriesData);

    // Fetch organization stats - ONLY REAL DATA
    try {
      // Get real user count for this organization

      const realUserCount = usersData?.data?.length || 0;

      // Get real organization count - use ID filter for organizations collection

      const realOrgCount = usersData?.data?.length || 0;

      setOrganizationStats({
        totalUsers: realUserCount,
        totalOrganizations: realOrgCount,
        activeProjects: totalMetrics,
        lastUpdated: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error fetching organization stats:", error);
      setOrganizationStats({
        totalUsers: 0,
        totalOrganizations: 0,
        activeProjects: totalMetrics,
        lastUpdated: new Date().toISOString(),
      });
    }

    // Generate real upcoming deadlines based on actual metrics
    const deadlines = metrics
      .filter(
        (m) =>
          m?.attributes?.currentCounter < (m?.attributes?.requiredCounter || 1)
      )
      .slice(0, 5)
      .map((metric, index) => {
        const current = metric?.attributes?.currentCounter || 0;
        const required = metric?.attributes?.requiredCounter || 1;
        const completionPercentage = (current / required) * 100;

        const priority = completionPercentage < 25 ? "high" : "medium";

        const createdAt = new Date(metric?.attributes?.createdAt);
        const daysSinceCreation = Math.floor(
          (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24)
        );

        const baseDeadline = new Date(
          createdAt.getTime() + 30 * 24 * 60 * 60 * 1000
        );
        const adjustedDeadline = new Date(
          baseDeadline.getTime() - daysSinceCreation * 24 * 60 * 60 * 1000
        );

        return {
          id: metric.id,
          title: metric?.attributes?.Name || "Unknown Metric",
          deadline: adjustedDeadline,
          priority: priority,
        };
      })
      .sort((a, b) => a.deadline - b.deadline);

    setUpcomingDeadlines(deadlines);

    // Generate real performance radar data based on ACTUAL categories
    const allCategories = [
      ...new Set(metrics.map((m) => m?.attributes?.Category).filter(Boolean)),
    ];

    const environmentalMetrics = metrics.filter(
      (m) =>
        m?.attributes?.Category?.toLowerCase() === "environmental" ||
        m?.attributes?.Category?.toLowerCase() === "environment"
    );

    const socialMetrics = metrics.filter(
      (m) => m?.attributes?.Category?.toLowerCase() === "social"
    );

    const governanceMetrics = metrics.filter(
      (m) => m?.attributes?.Category?.toLowerCase() === "governance"
    );

    const referenceDataMetrics = metrics.filter(
      (m) =>
        m?.attributes?.Category?.toLowerCase() === "reference data" ||
        m?.attributes?.Category?.toLowerCase() === "reference-data" ||
        m?.attributes?.Category?.toLowerCase() === "referencedata"
    );

    const calculateCategoryScore = (categoryMetrics) => {
      if (categoryMetrics.length === 0) return 0;
      const completed = categoryMetrics.filter(
        (m) =>
          (m?.attributes?.currentCounter || 0) >=
          (m?.attributes?.requiredCounter || 1)
      ).length;
      return Math.round((completed / categoryMetrics.length) * 100);
    };

    const radarData = [];
    const overallTarget = Math.min(100, Math.max(70, completionRate + 15));

    if (environmentalMetrics.length > 0) {
      radarData.push({
        subject: "Environmental",
        A: calculateCategoryScore(environmentalMetrics),
        B: overallTarget,
      });
    }

    if (socialMetrics.length > 0) {
      radarData.push({
        subject: "Social",
        A: calculateCategoryScore(socialMetrics),
        B: overallTarget,
      });
    }

    if (governanceMetrics.length > 0) {
      radarData.push({
        subject: "Governance",
        A: calculateCategoryScore(governanceMetrics),
        B: overallTarget,
      });
    }

    if (referenceDataMetrics.length > 0) {
      radarData.push({
        subject: "Reference Data",
        A: calculateCategoryScore(referenceDataMetrics),
        B: overallTarget,
      });
    }

    setPerformanceData(radarData);
    setLastDataRefresh(new Date());
  }, [metricsData, usersData, usersData]);

  const generateTimeframeChartData = (metrics, timeframe) => {
    const now = new Date();
    let labels = [];
    let dataPoints = [];

    switch (timeframe) {
      case "week":
        for (let i = 6; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(date.getDate() - i);
          labels.push(date.toLocaleDateString("en-US", { weekday: "short" }));

          const dayMetrics = metrics.filter((metric) => {
            const updatedAt = new Date(metric?.attributes?.updatedAt);
            return updatedAt.toDateString() === date.toDateString();
          });

          const completed = dayMetrics.filter(
            (m) =>
              (m?.attributes?.currentCounter || 0) >=
              (m?.attributes?.requiredCounter || 1)
          ).length;

          dataPoints.push({
            day: labels[labels.length - 1],
            completed,
            inProgress: dayMetrics.filter((m) => {
              const current = m?.attributes?.currentCounter || 0;
              const required = m?.attributes?.requiredCounter || 1;
              return current > 0 && current < required;
            }).length,
            pending: dayMetrics.filter(
              (m) => (m?.attributes?.currentCounter || 0) === 0
            ).length,
          });
        }
        break;

      case "month":
        for (let i = 29; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(date.getDate() - i);
          labels.push(date.getDate().toString());

          const dayMetrics = metrics.filter((metric) => {
            const updatedAt = new Date(metric?.attributes?.updatedAt);
            return updatedAt.toDateString() === date.toDateString();
          });

          const completed = dayMetrics.filter(
            (m) =>
              (m?.attributes?.currentCounter || 0) >=
              (m?.attributes?.requiredCounter || 1)
          ).length;

          dataPoints.push({
            day: labels[labels.length - 1],
            completed,
            inProgress: dayMetrics.filter((m) => {
              const current = m?.attributes?.currentCounter || 0;
              const required = m?.attributes?.requiredCounter || 1;
              return current > 0 && current < required;
            }).length,
            pending: dayMetrics.filter(
              (m) => (m?.attributes?.currentCounter || 0) === 0
            ).length,
          });
        }
        break;

      case "year":
        for (let i = 11; i >= 0; i--) {
          const date = new Date(now);
          date.setMonth(date.getMonth() - i);
          labels.push(date.toLocaleDateString("en-US", { month: "short" }));

          const monthMetrics = metrics.filter((metric) => {
            const updatedAt = new Date(metric?.attributes?.updatedAt);
            return (
              updatedAt.getMonth() === date.getMonth() &&
              updatedAt.getFullYear() === date.getFullYear()
            );
          });

          const completed = monthMetrics.filter(
            (m) =>
              (m?.attributes?.currentCounter || 0) >=
              (m?.attributes?.requiredCounter || 1)
          ).length;

          dataPoints.push({
            month: labels[labels.length - 1],
            completed,
            inProgress: monthMetrics.filter((m) => {
              const current = m?.attributes?.currentCounter || 0;
              const required = m?.attributes?.requiredCounter || 1;
              return current > 0 && current < required;
            }).length,
            pending: monthMetrics.filter(
              (m) => (m?.attributes?.currentCounter || 0) === 0
            ).length,
          });
        }
        break;

      default:
        return [];
    }

    return dataPoints;
  };

  const navigateHandler = (metric, id) => {
    const category = categoryName[metric]?.toLowerCase().replaceAll(" ", "-");
    const Navigator = metric?.toLowerCase().replaceAll(" ", "-");
    if (category === "reference-data" || category === "framework") {
      navigator(`/${currOrg}/${category}/${Navigator}/${id}`);
    } else navigator(`/${currOrg}/metrics/${category}/${Navigator}/${id}`);
  };

  // useEffect(() => {
  //   const fetchDataWithFilterUrl = async () => {
  //     getDataWithFilterUrlwithLimit(
  //       "responses",
  //       `${orgFilterUrl}&sort[0]=updatedAt:desc&pagination[limit]=8`
  //     )
  //       .then((data) => {
  //         setResponseData(data?.data);
  //         setRecentActivity(data?.data || []);
  //       })
  //       .catch((err) => {
  //         console.log(err);
  //       });
  //   };

  //   fetchDataWithFilterUrl();
  // }, [orgFilterUrl]);

  const { data: responses } = useQuery({
    queryKey: ["responseAndActivityData", orgFilterUrl], // refetches when this changes
    queryFn: () =>
      getDataWithFilterUrlwithLimit(
        "responses",
        `${orgFilterUrl}&sort[0]=updatedAt:desc&pagination[limit]=8`
      ),
  });

  // useEffect(() => {
  //   const categoryList = {};
  //   const result = responseData.map(async (item) => {
  //     const url =
  //       orgFilterUrl + "filters[Name][$eqi]=" + item?.attributes?.Metric_Id;
  //     const response = await getFilterDataWithPagination("metrics", url, 1, 1);
  //     categoryList[item?.attributes?.Metric_Id] =
  //       response?.data[0]?.attributes?.Category || "Unknown";
  //     return response;
  //   });

  //   Promise.all(result).then(() => {
  //     setCategoryName(categoryList);
  //   });
  // }, [responseData, orgFilterUrl]);

  const { data: MatricsList } = useQuery({
    queryKey: ["CategoryName", orgFilterUrl],
    queryFn: () => getFilterDataWithPagination("metrics", orgFilterUrl),
  });

  const categoryName = useMemo(() => {
    if (!MatricsList?.data || !responseData?.length) return {};

    // Create a map of metric name -> category
    const metricMap = {};
    MatricsList?.data?.map((metric) => {
      metricMap[metric?.attributes?.Name] =
        metric?.attributes?.Category || "Unknown";
    });

    // Build categoryList in key:value form
    const list = {};
    responseData?.map((item) => {
      const metricId = item?.attributes?.Metric_Id;
      list[metricId] = metricMap[metricId] || "Unknown";
    });

    return list;
  }, [MatricsList, responseData]);

  useEffect(() => {
    if (responses?.data) {
      setResponseData(responses.data);
      setRecentActivity(responses.data || []);
    }
  }, [responses]);

  const formatDeadline = (date) => {
    const now = new Date();
    const deadline = new Date(date);
    const diffTime = deadline - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "Overdue";
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays <= 7) return `${diffDays} days`;
    return deadline.toLocaleDateString();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const event = new Event("refresh-dashboard");
    window.dispatchEvent(event);
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const getTimeframeLabel = () => {
    switch (selectedTimeframe) {
      case "week":
        return "Last 7 Days";
      case "month":
        return "Last 30 Days";
      case "year":
        return "Last 12 Months";
      default:
        return "Last 30 Days";
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 shadow-sm">
        <div className="max-w-full">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Dashboard</h1>
              <p className="text-sm text-slate-600 mt-1">
                Welcome back, {UserDetails?.FirstName || UserDetails?.email}!
                Here's your ESG overview.
                {lastDataRefresh && (
                  <span className="text-xs text-slate-500 ml-2">
                    • Last updated: {lastDataRefresh.toLocaleTimeString()}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      <BreadCrumbsPage>
        {isPending ? (
          <PageLoader
            loadingText="Loading Dashboard..."
            description="Fetching user data and organization metrics"
            variant="default"
          />
        ) : (
          <div className="w-full px-6 py-6">
            {/* Metrics Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <MetricSummaryCard
                title="Total Metrics"
                value={metricsSummary.totalMetrics}
                change={
                  metricsSummary.totalMetrics > 0
                    ? Math.round(
                        (metricsSummary.totalMetrics /
                          Math.max(metricsSummary.totalMetrics, 1)) *
                          100
                      )
                    : 0
                }
                icon={faChartLine}
                color="emerald"
                subtitle="Across all categories"
                trend={metricsSummary.totalMetrics > 0 ? "up" : "neutral"}
              />
              <MetricSummaryCard
                title="Completed"
                value={metricsSummary.completedMetrics}
                change={metricsSummary.completionRate}
                icon={faCheckCircle}
                color="blue"
                subtitle={`${metricsSummary.completionRate}% completion rate`}
                trend={metricsSummary.completedMetrics > 0 ? "up" : "neutral"}
              />
              <MetricSummaryCard
                title="In Progress"
                value={metricsSummary.inProgressMetrics}
                change={
                  metricsSummary.inProgressMetrics > 0
                    ? Math.round(
                        (metricsSummary.inProgressMetrics /
                          Math.max(metricsSummary.totalMetrics, 1)) *
                          100
                      )
                    : 0
                }
                icon={faClock}
                color="orange"
                subtitle="Currently being worked on"
                trend={metricsSummary.inProgressMetrics > 0 ? "up" : "neutral"}
              />
              <MetricSummaryCard
                title="Pending"
                value={metricsSummary.pendingMetrics}
                change={
                  metricsSummary.pendingMetrics > 0
                    ? Math.round(
                        (metricsSummary.pendingMetrics /
                          Math.max(metricsSummary.totalMetrics, 1)) *
                          100
                      )
                    : 0
                }
                icon={faExclamationTriangle}
                color="red"
                subtitle="Not yet started"
                trend={metricsSummary.pendingMetrics > 0 ? "down" : "neutral"}
              />
            </div>

            {/* Main Dashboard Content */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Left Column - Charts and Activity */}
              <div className="xl:col-span-2 space-y-6">
                {/* Progress Chart with Time Filter */}
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader className="pb-0 pt-4 px-4">
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <h3 className="text-base font-semibold text-slate-900">
                          Progress Overview
                        </h3>
                        <p className="text-xs text-slate-600 mt-1">
                          {getTimeframeLabel()} •{" "}
                          {chartData.some(
                            (item) =>
                              item.completed > 0 ||
                              item.inProgress > 0 ||
                              item.pending > 0
                          )
                            ? "Real metrics data from your organization"
                            : "Add metrics to see trends"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="light"
                          onClick={handleRefresh}
                          isLoading={isRefreshing}
                        >
                          <FontAwesomeIcon icon={faRefresh} />
                        </Button>
                        <Chip
                          size="sm"
                          color={
                            chartData.some(
                              (item) =>
                                item.completed > 0 ||
                                item.inProgress > 0 ||
                                item.pending > 0
                            )
                              ? "success"
                              : "warning"
                          }
                          variant="flat"
                        >
                          {chartData.some(
                            (item) =>
                              item.completed > 0 ||
                              item.inProgress > 0 ||
                              item.pending > 0
                          )
                            ? "Live Data"
                            : "No Data"}
                        </Chip>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody className="px-4 pb-4">
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e2e8f0"
                          />
                          <XAxis
                            dataKey={
                              selectedTimeframe === "year" ? "month" : "day"
                            }
                            stroke="#64748b"
                            fontSize={10}
                          />
                          <YAxis stroke="#64748b" fontSize={10} />
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: "white",
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                              fontSize: "12px",
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="completed"
                            stackId="1"
                            stroke="#059669"
                            fill="#059669"
                            fillOpacity={0.6}
                            name="Completed"
                          />
                          <Area
                            type="monotone"
                            dataKey="inProgress"
                            stackId="1"
                            stroke="#ea580c"
                            fill="#ea580c"
                            fillOpacity={0.6}
                            name="In Progress"
                          />
                          <Area
                            type="monotone"
                            dataKey="pending"
                            stackId="1"
                            stroke="#dc2626"
                            fill="#dc2626"
                            fillOpacity={0.6}
                            name="Pending"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardBody>
                </Card>

                {/* Performance Overview */}
                {/* <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader className="pb-0 pt-4 px-4">
                    <h3 className="text-base font-semibold text-slate-900">Performance Overview</h3>
                    <p className="text-xs text-slate-600 mt-1">ESG category performance</p>
                  </CardHeader>
                  <CardBody className="px-4 pb-4">
                    <PerformanceRadarChart data={performanceData} />
                  </CardBody>
                </Card> */}

                {/* Completion Status */}
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader className="pb-0 pt-4 px-4">
                    <h3 className="text-base font-semibold text-slate-900">
                      Completion Status
                    </h3>
                    {/* <p className="text-xs text-slate-600 mt-1">Overall progress</p> */}
                  </CardHeader>
                  <CardBody className="px-4 pb-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-700">
                          Completed
                        </span>
                        <span className="text-sm font-semibold text-slate-900">
                          {metricsSummary.completedMetrics}
                        </span>
                      </div>
                      <Progress
                        value={metricsSummary.completionRate}
                        color="success"
                        className="h-2"
                        showValueLabel={false}
                      />
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>
                          In Progress: {metricsSummary.inProgressMetrics}
                        </span>
                        <span>Pending: {metricsSummary.pendingMetrics}</span>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                {/* Top Categories */}
                {/* <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader className="pb-0 pt-4 px-4">
                    <h3 className="text-base font-semibold text-slate-900">Top Categories</h3>
                    <p className="text-xs text-slate-600 mt-1">Most active metric categories</p>
                  </CardHeader>
                  <CardBody className="px-4 pb-4">
                    <div className="space-y-3">
                      {topCategories.map((item, index) => (
                        <div key={item.category} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                              <span className="text-emerald-600 text-xs font-semibold">{index + 1}</span>
                            </div>
                            <span className="text-sm text-slate-700">{item.category}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Progress 
                              value={(item.count / Math.max(...topCategories.map(c => c.count))) * 100} 
                              className="w-16 h-2"
                              color="success"
                            />
                            <span className="text-xs text-slate-600 w-8 text-right">{item.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardBody>
                </Card> */}

                {/* Category Breakdown */}
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader className="pb-0 pt-4 px-4">
                    <h3 className="text-base font-semibold text-slate-900">
                      Category Breakdown
                    </h3>
                  </CardHeader>
                  <CardBody className="px-4 pb-4">
                    <div className="space-y-3">
                      {topCategories.slice(0, 4).map((item, index) => {
                        const percentage = Math.round(
                          (item.count / metricsSummary.totalMetrics) * 100
                        );
                        return (
                          <div
                            key={item.category}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-3 h-3 rounded-full bg-${
                                  index === 0
                                    ? "emerald"
                                    : index === 1
                                    ? "blue"
                                    : index === 2
                                    ? "orange"
                                    : "purple"
                                }-500`}
                              ></div>
                              <span className="text-sm text-slate-700">
                                {item.category}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-slate-900">
                                {item.count}
                              </span>
                              <span className="text-xs text-slate-500">
                                ({percentage}%)
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardBody>
                </Card>

                {/* Recent Activity */}
                <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden">
                  <CardHeader className="pb-0 pt-4 px-4">
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <h3 className="text-base font-semibold text-slate-900">
                          Recent Activity
                        </h3>
                        {/* <p className="text-xs text-slate-600 mt-1">Latest metric updates</p> */}
                      </div>
                      <Tooltip content="View all activity">
                        <Link
                          onClick={() => navigator(`/${currOrg}/audit-logs`)}
                          showAnchorIcon
                          className="text-emerald-600 hover:text-emerald-700 cursor-pointer text-xs font-medium"
                        >
                          View All
                        </Link>
                      </Tooltip>
                    </div>
                  </CardHeader>
                  <CardBody className="px-4 pb-4">
                    <div className="space-y-3">
                      {recentActivity?.slice(0, 5).map((data) => {
                        const colorIndex =
                          data?.attributes?.users_permissions_user?.data?.id %
                          numColors;
                        const currentCount =
                          data?.attributes?.currentCounter || 0;
                        const requiredCount =
                          data?.attributes?.requiredCounter || 1;
                        const status =
                          currentCount >= requiredCount
                            ? "Completed"
                            : currentCount > 0
                            ? "In Progress"
                            : "Not Started";
                        const statusColor =
                          currentCount >= requiredCount
                            ? "success"
                            : currentCount > 0
                            ? "warning"
                            : "danger";

                        return (
                          <div
                            key={data?.id || data?.attributes?.Metric_Id}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                            onClick={() =>
                              navigateHandler(
                                data?.attributes?.Metric_Id,
                                data?.id
                              )
                            }
                          >
                            <Avatar
                              className="h-8 w-8"
                              color={Colors[colorIndex]}
                              name={data?.attributes?.users_permissions_user?.data?.attributes?.email
                                .toUpperCase()
                                .slice(0, 1)}
                              src={
                                data?.attributes?.users_permissions_user?.data
                                  ?.attributes?.ProfilePicture &&
                                `${process.env.REACT_APP_STRAPI_IP_ADDRESS}${data?.attributes?.users_permissions_user?.data?.attributes?.ProfilePicture.url}`
                              }
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">
                                {data?.attributes?.Metric_Id}
                              </p>
                              <p className="text-xs text-slate-500">
                                {DateFormatorDD(data?.attributes?.updatedAt)} •{" "}
                                {categoryName[data?.attributes?.Metric_Id] ||
                                  "Unknown"}
                              </p>
                            </div>
                            <Chip size="sm" color={statusColor} variant="flat">
                              {status}
                            </Chip>
                          </div>
                        );
                      })}
                      {recentActivity?.length === 0 && (
                        <div className="text-center py-4">
                          <p className="text-sm text-slate-500">
                            No recent activity
                          </p>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </div>

              {/* Right Column - Quick Actions and Stats */}
              <div className="space-y-6">
                {/* Quick Actions */}
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader className="pb-0 pt-4 px-4">
                    <h3 className="text-base font-semibold text-slate-900">
                      Quick Actions
                    </h3>
                    {/* <p className="text-xs text-slate-600 mt-1">Common tasks</p> */}
                  </CardHeader>
                  <CardBody className="px-4 pb-4">
                    <div className="space-y-3">
                      <QuickActionCard
                        title="Create Metric"
                        description="Add a new ESG metric"
                        icon={faPlus}
                        color="emerald"
                        onClick={() => navigator(`/${currOrg}/metrics-builder`)}
                        badge={
                          metricsSummary.pendingMetrics > 0
                            ? metricsSummary.pendingMetrics
                            : undefined
                        }
                      />
                      <QuickActionCard
                        title="View Analytics"
                        description="Detailed insights and reports"
                        icon={faChartLine}
                        color="blue"
                        onClick={() => navigator(`/${currOrg}/analytics`)}
                      />
                      <QuickActionCard
                        title="Generate Report"
                        description="Create ESG compliance report"
                        icon={faFileAlt}
                        color="orange"
                        onClick={() => navigator(`/${currOrg}/report`)}
                      />
                      <QuickActionCard
                        title="Settings"
                        description="Configure your workspace"
                        icon={faCog}
                        color="gray"
                        onClick={() => navigator(`/${currOrg}/settings`)}
                      />
                    </div>
                  </CardBody>
                </Card>

                {/* Organization Stats */}
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader className="pb-0 pt-4 px-4">
                    <h3 className="text-base font-semibold text-slate-900">
                      Organization Stats
                    </h3>
                    {/* <p className="text-xs text-slate-600 mt-1">Overview of your workspace</p> */}
                  </CardHeader>
                  <CardBody className="px-4 pb-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon
                            icon={faUsers}
                            className="text-emerald-500 text-sm"
                          />
                          <span className="text-sm text-slate-700">
                            Total Users
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-slate-900">
                          {organizationStats.totalUsers}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon
                            icon={faChartLine}
                            className="text-emerald-500 text-sm"
                          />
                          <span className="text-sm text-slate-700">
                            Total Metrics
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-slate-900">
                          {organizationStats.activeProjects}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon
                            icon={faCalendarAlt}
                            className="text-orange-500 text-sm"
                          />
                          <span className="text-sm text-slate-700">
                            Last Updated
                          </span>
                        </div>
                        <span className="text-xs text-slate-600">
                          {organizationStats.lastUpdated
                            ? new Date(
                                organizationStats.lastUpdated
                              ).toLocaleDateString()
                            : "Never"}
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                {/* Recent Metrics Activity */}
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader className="pb-0 pt-4 px-4">
                    <h3 className="text-base font-semibold text-slate-900">
                      Recent Metrics Activity
                    </h3>
                  </CardHeader>
                  <CardBody className="px-4 pb-4">
                    <div className="space-y-3">
                      {recentActivity?.slice(0, 3).map((data, index) => (
                        <div
                          key={data?.id || index}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                        >
                          <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {data?.attributes?.Metric_Id || "Unknown Metric"}
                            </p>
                            <p className="text-xs text-slate-500">
                              {DateFormatorDD(data?.attributes?.updatedAt)} •{" "}
                              {categoryName[data?.attributes?.Metric_Id] ||
                                "Unknown"}
                            </p>
                          </div>
                        </div>
                      ))}
                      {recentActivity?.length === 0 && (
                        <div className="text-center py-4">
                          <p className="text-sm text-slate-500">
                            No recent activity
                          </p>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>

                {/* Upcoming Deadlines */}
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader className="pb-0 pt-4 px-4">
                    <h3 className="text-base font-semibold text-slate-900">
                      Upcoming Deadlines
                    </h3>
                  </CardHeader>
                  <CardBody className="px-4 pb-4">
                    <div className="space-y-3">
                      {upcomingDeadlines.map((deadline) => (
                        <div
                          key={deadline.id}
                          className="flex items-center justify-between p-2 rounded-lg bg-slate-50"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {deadline.title}
                            </p>
                            <p className="text-xs text-slate-500">
                              Due {formatDeadline(deadline.deadline)}
                            </p>
                          </div>
                          <Chip
                            size="sm"
                            color={
                              deadline.priority === "high"
                                ? "danger"
                                : "warning"
                            }
                            variant="flat"
                          >
                            {deadline.priority}
                          </Chip>
                        </div>
                      ))}
                      {upcomingDeadlines.length === 0 && (
                        <div className="text-center py-4">
                          <p className="text-sm text-slate-500">
                            No upcoming deadlines
                          </p>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </div>
            </div>
          </div>
        )}
      </BreadCrumbsPage>
    </div>
  );
};

export default Dashboard;
